import { trackEvent } from '../analytics';
import posthog from 'posthog-js';
import { axios } from '@/axios';

// Attribution sources mapping
export const ATTRIBUTION_SOURCES = {
  tiktok: 'TikTok',
  reddit: 'Reddit', 
  x: 'X (Twitter)',
  youtube: 'YouTube',
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  discord: 'Discord',
  telegram: 'Telegram',
  hackernews: 'Hacker News',
  producthunt: 'Product Hunt',
  google: 'Google Ads',
  direct: 'Direct',
  organic: 'Organic Search',
  email: 'Email',
  referral: 'Referral'
} as const;

export type AttributionSource = keyof typeof ATTRIBUTION_SOURCES;

interface AttributionData {
  source: string;
  campaign?: string;
  medium?: string;
  content?: string;
  term?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  timestamp: number;
  referrer?: string;
  userAgent: string;
  url: string;
}

// Store attribution data in localStorage with expiry
const ATTRIBUTION_KEY = 'moonscape_attribution';
// Raw referrer + URL of the first page load in this tab, taken in index.js
// before React mounts. sessionStorage survives the same-tab Auth0 round trip,
// so the post-login load still sees where the visit really started.
const FIRST_LOAD_KEY = 'moonscape_first_load';

/**
 * Hosts that are hops inside our own sign-in flow, never where a user came
 * from: Auth0 (the callback and Auth0-initiated /login?iss=... land here with
 * referrer = the tenant) and our own sites (marketing -> dashboard handoff).
 */
const isOwnHost = (host: string) =>
  host === 'moonscape.app' || host.endsWith('.moonscape.app') || host.endsWith('.auth0.com');

/** The referrer, or '' when it is empty, unparsable or one of our own hops. */
export const externalReferrer = (referrer: string = document.referrer): string => {
  try {
    return referrer && !isOwnHost(new URL(referrer).hostname.toLowerCase()) ? referrer : '';
  } catch {
    return '';
  }
};
const ATTRIBUTION_EXPIRY = 30 * 24 * 60 * 60 * 1000; // 30 days

export class AttributionTracker {
  static setAttribution(data: Partial<AttributionData>) {
    const attribution: AttributionData = {
      source: data.source || 'direct',
      campaign: data.campaign,
      medium: data.medium,
      content: data.content,
      term: data.term,
      timestamp: Date.now(),
      referrer: data.referrer || externalReferrer() || undefined,
      userAgent: navigator.userAgent,
      url: window.location.href,
      ...data
    };

    // Store with expiry
    const attributionWithExpiry = {
      data: attribution,
      expiry: Date.now() + ATTRIBUTION_EXPIRY
    };

    try { localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attributionWithExpiry)); } catch { /* Storage may be disabled. */ }
    
    // Track the attribution event with both Segment and PostHog
    trackEvent('Attribution Captured', {
      source: attribution.source,
      campaign: attribution.campaign,
      medium: attribution.medium,
      referrer: attribution.referrer,
      url: attribution.url
    });

    // PostHog owns its native $initial_* properties. Our fallback after Auth0
    // can be "direct" and must never overwrite the landing page's first touch.

    console.log('📍 Attribution set:', attribution);
    return attribution;
  }

  static getAttribution(): AttributionData | null {
    try {
      const stored = localStorage.getItem(ATTRIBUTION_KEY);
      if (!stored) return null;

      const { data, expiry } = JSON.parse(stored);
      
      // Check if expired
      if (Date.now() > expiry) {
        localStorage.removeItem(ATTRIBUTION_KEY);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error getting attribution:', error);
      try { localStorage.removeItem(ATTRIBUTION_KEY); } catch { /* Storage unavailable. */ }
      return null;
    }
  }

  static clearAttribution() {
    localStorage.removeItem(ATTRIBUTION_KEY);
  }

  // Parse URL parameters for attribution
  static parseUrlForAttribution(url: string = window.location.href): Partial<AttributionData> {
    const urlObj = new URL(url);
    const params = urlObj.searchParams;
    
    // Check for UTM parameters
    const utmSource = params.get('utm_source');
    const utmCampaign = params.get('utm_campaign');
    const utmMedium = params.get('utm_medium');
    const utmContent = params.get('utm_content');
    const utmTerm = params.get('utm_term');

    // Check for custom source parameter
    const source = params.get('source') || params.get('ref');
    
    return {
      source: utmSource || source || (params.has('gclid') || params.has('gbraid') || params.has('wbraid') ? 'google' : undefined),
      campaign: utmCampaign || undefined,
      medium: utmMedium || (params.has('gclid') || params.has('gbraid') || params.has('wbraid') ? 'cpc' : undefined),
      content: utmContent || undefined,
      term: utmTerm || undefined,
      gclid: params.get('gclid') || undefined,
      gbraid: params.get('gbraid') || undefined,
      wbraid: params.get('wbraid') || undefined,
    };
  }

  // Detect attribution from referrer
  static detectAttributionFromReferrer(referrer: string = document.referrer): string {
    if (!referrer) return 'direct';

    let domain: string;
    try { domain = new URL(referrer).hostname.toLowerCase(); } catch { return 'direct'; }
    // Auth0 and our own sites are hops, not sources (see isOwnHost)
    if (isOwnHost(domain)) return 'internal';
    
    // Social media platforms
    if (domain.includes('tiktok.com')) return 'tiktok';
    if (domain.includes('reddit.com')) return 'reddit';
    if (domain.includes('twitter.com') || domain.includes('t.co') || domain.includes('x.com')) return 'x';
    if (domain.includes('youtube.com')) return 'youtube';
    if (domain.includes('instagram.com')) return 'instagram';
    if (domain.includes('facebook.com') || domain.includes('fb.com')) return 'facebook';
    if (domain.includes('linkedin.com')) return 'linkedin';
    if (domain.includes('discord.com') || domain.includes('discord.gg')) return 'discord';
    if (domain.includes('telegram.org') || domain.includes('t.me')) return 'telegram';
    if (domain.includes('news.ycombinator.com')) return 'hackernews';
    if (domain.includes('producthunt.com')) return 'producthunt';
    
    // Search engines
    if (domain.includes('google.com')) return 'google';
    if (domain.includes('bing.com')) return 'bing';
    if (domain.includes('yahoo.com')) return 'yahoo';
    if (domain.includes('duckduckgo.com')) return 'duckduckgo';
    
    // If it's your own domain, it's internal
    if (domain.includes('moonscape.app')) return 'internal';
    
    return 'referral';
  }

  /** Call once, synchronously, before anything can redirect (index.js). First call per tab wins. */
  static snapshotFirstLoad() {
    try {
      if (sessionStorage.getItem(FIRST_LOAD_KEY)) return;
      sessionStorage.setItem(FIRST_LOAD_KEY, JSON.stringify({ referrer: document.referrer, url: window.location.href }));
    } catch { /* Storage may be disabled; init() falls back to the live values. */ }
  }

  static firstLoad(): { referrer: string; url: string } {
    try {
      const stored = sessionStorage.getItem(FIRST_LOAD_KEY);
      if (stored) return JSON.parse(stored);
    } catch { /* fall through */ }
    return { referrer: document.referrer, url: window.location.href };
  }

  // Initialize attribution tracking
  static init() {
    const currentAttribution = this.getAttribution();
    const first = this.firstLoad();
    // Prefer the pre-auth landing URL's params; fall back to the live URL
    // (a tagged link opened later in the same tab).
    const firstUrlAttribution = this.parseUrlForAttribution(first.url);
    const urlAttribution = firstUrlAttribution.source ? firstUrlAttribution : this.parseUrlForAttribution();
    const referrer = externalReferrer(first.referrer);
    
    // If we already have valid attribution, don't override it (first-touch attribution)
    if (currentAttribution && !(urlAttribution.source && ['direct', 'internal'].includes(currentAttribution.source))) {
      console.log('📍 Existing attribution found:', currentAttribution);
      return currentAttribution;
    }

    // Parse URL for attribution parameters
    // Detect from referrer if no URL parameters
    const referrerSource = this.detectAttributionFromReferrer(referrer || first.referrer);

    const attribution = this.setAttribution({
      referrer: referrer || undefined,
      // landing_path: the page the tag came from, else the first page of the visit
      url: firstUrlAttribution.source || !urlAttribution.source ? first.url : window.location.href,
      source: urlAttribution.source || referrerSource,
      campaign: urlAttribution.campaign,
      medium: urlAttribution.medium,
      content: urlAttribution.content,
      term: urlAttribution.term,
      gclid: urlAttribution.gclid,
      gbraid: urlAttribution.gbraid,
      wbraid: urlAttribution.wbraid
    });

    return attribution;
  }

  // Track conversion event with attribution data
  static trackConversion(userId: string, eventName: string = 'User Converted') {
    const attribution = this.getAttribution();
    if (!attribution) return;

    const timeToConversion = Date.now() - attribution.timestamp;

    // Track conversion with attribution context
    trackEvent(eventName, {
      userId,
      source: attribution.source,
      campaign: attribution.campaign,
      medium: attribution.medium,
      time_to_conversion_ms: timeToConversion,
      time_to_conversion_hours: Math.round(timeToConversion / (1000 * 60 * 60) * 100) / 100
    });

    // Set PostHog conversion properties
    posthog.setPersonProperties({
      $converted: true,
      $conversion_source: attribution.source,
      $conversion_campaign: attribution.campaign,
      $time_to_conversion_ms: timeToConversion
    });

    console.log(`📍 Conversion tracked: ${eventName} for ${userId}`);
  }

  // Send captured attribution to the server so the signup carries a source in
  // Postgres, not just in PostHog. Writes users.signup_source (first touch
  // only) and appends a web_utm_conversions row.
  //
  // Must go through the app's axios instance: the endpoint sits behind checkJWT
  // and only that instance carries the Auth0 bearer token and the API base URL.
  // A bare fetch() to a relative path hits the web origin unauthenticated.
  static async sendConversionToServer(conversionType: string = 'signup', accessToken: string): Promise<void> {
    const attribution = this.getAttribution();
    if (!attribution) {
      console.warn('\u{1F4CD} No attribution to send to server');
      return;
    }

    const { data } = await axios.post('/web/utm-conversion', {
      utm_source: attribution.source,
      utm_campaign: attribution.campaign,
      utm_medium: attribution.medium,
      utm_content: attribution.content,
      utm_term: attribution.term,
      gclid: attribution.gclid,
      gbraid: attribution.gbraid,
      wbraid: attribution.wbraid,
      referrer: attribution.referrer,
      initial_referrer: attribution.referrer,
      landing_path: attribution.url,
      platform: 'web',
      captured_at: new Date(attribution.timestamp).toISOString(),
      conversion_type: conversionType,
    }, { headers: { Authorization: `Bearer ${accessToken}` } });
    console.log('\u{1F4CD} Attribution sent to server:', data?.result);
  }

}
